import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useReducedMotion, useInView, AnimatePresence } from 'framer-motion';
import './EvalSection.css';

const PROMPT_TEXT = `Create a 3D promotional animation of a BBQ rib being dipped into sauce using Blender with Python scripting.

Product: "Rib-n-Dip" — a custom elongated BBQ sauce cup designed for dipping full rib bones. The cup is white with embossed branding and has notched handles on both short sides.

Assets provided: Two GLB models — rib_light.glb (a realistic grilled bone-in pork rib) and cup_low_poly.glb (the elongated sauce cup).

Animation: A single smooth arc motion — the rib starts above the cup, dips down into the sauce, and lifts back out coated in sauce. Simple, clean, no pauses. The motion should feel like a satisfying product demo.

Sauce: Thick, glossy, viscous BBQ sauce filling the cup. Dark reddish-brown color, wet glossy surface with visible light reflections. Should behave like real BBQ sauce — heavy, sticky, coats the rib when lifted. Use Blender's fluid simulation (Mantaflow). When the rib lifts out, thick teardrop-shaped drips should hang from the underside — not thin threads, but heavy viscous drops like real BBQ sauce.

Look: Cinematic product-shot aesthetic. Dark moody studio background, professional 3-point lighting setup (key, fill, rim). Camera at a 3/4 angle showing the cup branding and the dipping action. Filmic color management. The sauce should be the hero — its glossy surface catching the light is what sells the product.

Output: Render as a looping video (1920x1080, 24fps) using Cycles with GPU rendering and denoising. The entire scene — setup, fluid simulation, materials, animation, lighting, camera, and rendering — should be driven by a single Python script run in Blender's background mode.`;

const CARDS = [
  { id: 'reference', label: 'Reference', badge: 'reference', sublabel: 'AI-Guided Blender Render', src: '/videos/reference-dip.mp4' },
  { id: 'claude', label: 'Claude', badge: 'claude', sublabel: 'Blender Render', src: '/videos/dip-animation.mp4' },
  { id: 'chatgpt', label: 'ChatGPT', badge: 'chatgpt', sublabel: 'Blender Render', src: '/videos/chatgpt-dip.mp4' },
  { id: 'gemini', label: 'Gemini', badge: 'gemini', sublabel: 'Blender Render', src: '/videos/gemini-dip.mp4' },
];

const EvalSection = () => {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  const [open, setOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const isVisible = useInView(contentRef, { amount: 0.2 });

  useEffect(() => { setIsClient(true); }, []);

  // Listen for custom "open-eval" event from the HomeBBQ button
  const handleToggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('toggle-eval', handleToggle);
    return () => window.removeEventListener('toggle-eval', handleToggle);
  }, [handleToggle]);

  useEffect(() => {
    for (const video of videoRefs.current) {
      if (!video) continue;
      if (open && isVisible && !prefersReducedMotion) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  }, [open, isVisible, prefersReducedMotion]);

  const showVideo = isClient && !prefersReducedMotion;

  return (
    <div id="eval" ref={contentRef}>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="eval"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="eval__inner">
              <div className="eval__header">
                <h2 className="eval__title">The Rib-n-Dip Eval</h2>
                <p className="eval__subtitle">
                  We gave three AI models the same prompt: render our product video in Blender. Here's what came back.
                </p>
              </div>

              <div className="eval__prompt">
                <button
                  className="eval__prompt-toggle"
                  onClick={() => setPromptOpen(!promptOpen)}
                  aria-expanded={promptOpen}
                >
                  <motion.span
                    className="eval__prompt-icon"
                    animate={{ rotate: promptOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                      <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </motion.span>
                  <span className="eval__prompt-label">The Prompt</span>
                </button>
                <AnimatePresence initial={false}>
                  {promptOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      style={{ overflow: 'hidden' }}
                    >
                      <pre className="eval__prompt-text">{PROMPT_TEXT}</pre>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="eval__grid">
                {CARDS.map((card, i) => (
                  <motion.div
                    key={card.id}
                    className={`eval__card${card.id === 'reference' ? ' eval__card--reference' : ''}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                  >
                    <div className="eval__card-header">
                      <span className={`eval__badge eval__badge--${card.badge}`}>{card.label}</span>
                      <span className="eval__sublabel">{card.sublabel}</span>
                    </div>
                    <div className="eval__card-video">
                      {showVideo ? (
                        <video
                          ref={(el) => { videoRefs.current[i] = el; }}
                          src={card.src}
                          loop
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <div className="eval__video-fallback">Animation paused</div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EvalSection;
